import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  TableContainer,
  Switch,
  FormControlLabel,
  MenuItem,
  Chip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PublicIcon from "@mui/icons-material/Public";
import CategoryIcon from "@mui/icons-material/Category";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import api from "../../utils/api";

// ─── Shared styles ────────────────────────────────────────────────────────────

const paperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const fieldSx = {
  mt: 1.5,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: "10px",
  },
};

const primaryButtonSx = {
  backgroundColor: "#2f63ad",
  color: "white",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  px: 2.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
};

// ─── Constants ────────────────────────────────────────────────────────────────

const BILLING_MODE_LABELS = {
  fine: "Fine",
  flat: "Flat Fee",
  per_occurrence: "Per Occurrence",
  escalating: "Escalating Fine",
};

const BILLING_MODE_COLORS = {
  fine: { bg: "rgba(239,68,68,0.1)", color: "#dc2626" },
  flat: { bg: "rgba(47,99,173,0.1)", color: "#2f63ad" },
  per_occurrence: { bg: "rgba(234,179,8,0.1)", color: "#b45309" },
  escalating: { bg: "rgba(168,85,247,0.1)", color: "#7c3aed" },
};

const TRIGGER_MODE_LABELS = {
  manual: "Manual",
  automatic: "Automatic",
};

const BLANK_FORM = {
  scope: "global",
  violation_type_id: "",
  integration_id: "",
  billing_mode: "fine",
  fine_amount: "",
  labor_rate: "",
  escalation_enabled: false,
  escalation_amount: "",
  max_fine: "",
  trigger_mode: "manual",
  trigger_status_id: "",
  billing_code: "",
  recurring_enabled: false,
  recurring_interval: "",
  recurring_frequency: "monthly",
  is_active: true,
  notes: "",
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ViolationBillingSetupManager = () => {
  const [settings, setSettings] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, typesRes, intRes, statusRes] = await Promise.all([
        api.get("/violation-billing-settings"),
        api.get("/getCcrViolationTypes"),
        api.get("/integrations"),
        api.get("/statuses?module_id=2").catch(() => ({ data: [] })),
      ]);
      setSettings(settingsRes.data || []);
      setViolationTypes(typesRes.data || []);
      setIntegrations(intRes.data || []);
      setStatuses(statusRes.data || []);
    } catch {
      showSnackbar("Failed to load data", "error");
    }
  };

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const resetForm = () => {
    setForm(BLANK_FORM);
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      scope: s.violation_type_id ? "type" : "global",
      violation_type_id: s.violation_type_id ?? "",
      integration_id: s.integration_id ?? "",
      billing_mode: s.billing_mode || "fine",
      fine_amount: s.fine_amount ?? "",
      labor_rate: s.labor_rate ?? "",
      escalation_enabled: Boolean(s.escalation_enabled),
      escalation_amount: s.escalation_amount ?? "",
      max_fine: s.max_fine ?? "",
      trigger_mode: s.trigger_mode || "manual",
      trigger_status_id: s.trigger_status_id ?? "",
      billing_code: s.billing_code ?? "",
      recurring_enabled: Boolean(s.recurring_enabled),
      recurring_interval: s.recurring_interval ?? "",
      recurring_frequency: s.recurring_frequency || "monthly",
      is_active: Boolean(s.is_active ?? 1),
      notes: s.notes || "",
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      const next = { ...p, [name]: value };
      if (name === "scope" && value === "global") next.violation_type_id = "";
      if (name === "billing_mode" && value !== "escalating") {
        next.escalation_enabled = false;
        next.escalation_amount = "";
        next.max_fine = "";
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (form.fine_amount === "" && form.billing_mode !== "per_occurrence") {
      showSnackbar("Please enter a fine amount", "error");
      return;
    }
    if (form.scope === "type" && !form.violation_type_id) {
      showSnackbar("Please select a violation type", "error");
      return;
    }

    const payload = {
      violation_type_id: form.scope === "type" ? Number(form.violation_type_id) : null,
      apply_to_all: form.scope === "global" ? 1 : 0,
      integration_id: form.integration_id ? Number(form.integration_id) : null,
      billing_mode: form.billing_mode,
      fine_amount: form.fine_amount !== "" ? Number(form.fine_amount) : null,
      labor_rate: form.labor_rate !== "" ? Number(form.labor_rate) : null,
      escalation_enabled: form.billing_mode === "escalating" ? Boolean(form.escalation_enabled) : false,
      escalation_amount: form.billing_mode === "escalating" && form.escalation_amount !== "" ? Number(form.escalation_amount) : null,
      max_fine: form.billing_mode === "escalating" && form.max_fine !== "" ? Number(form.max_fine) : null,
      trigger_mode: form.trigger_mode,
      trigger_status_id: form.trigger_status_id ? Number(form.trigger_status_id) : null,
      billing_code: form.billing_code?.trim() || null,
      recurring_enabled: Boolean(form.recurring_enabled),
      recurring_interval: form.recurring_enabled && form.recurring_interval !== "" ? Number(form.recurring_interval) : null,
      recurring_frequency: form.recurring_enabled ? (form.recurring_frequency || "monthly") : null,
      is_active: Boolean(form.is_active),
      notes: form.notes?.trim() || null,
    };

    try {
      if (editingId) {
        await api.put(`/violation-billing-settings/${editingId}`, payload);
        showSnackbar("Billing rule updated");
      } else {
        await api.post("/violation-billing-settings", payload);
        showSnackbar("Billing rule added");
      }
      await fetchAll();
      closeDialog();
    } catch {
      showSnackbar("Error saving billing rule", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this billing rule?")) return;
    try {
      await api.delete(`/violation-billing-settings/${id}`);
      showSnackbar("Billing rule deleted");
      fetchAll();
    } catch {
      showSnackbar("Error deleting billing rule", "error");
    }
  };

  const getTypeLabel = (typeId) => {
    if (!typeId) return "All Types";
    return (
      violationTypes.find((t) => Number(t.id) === Number(typeId))?.title || `Type #${typeId}`
    );
  };

  const getIntegrationLabel = (integrationId) => {
    if (!integrationId) return "N/A";
    return (
      integrations.find((i) => Number(i.id) === Number(integrationId))?.name ||
      `Integration #${integrationId}`
    );
  };

  const getModeColor = (mode) => BILLING_MODE_COLORS[mode] || BILLING_MODE_COLORS.fine;

  const showEscalation = form.billing_mode === "escalating";

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Violation Billing Setup
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Configure fine amounts, escalation rules, and billing triggers per violation type.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={primaryButtonSx}
        >
          Add Billing Rule
        </Button>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={paperSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Violation Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Billing Mode</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fine / Rate</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Integration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.length > 0 ? (
                settings.map((s) => {
                  const mc = getModeColor(s.billing_mode);
                  return (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Chip
                          icon={s.violation_type_id ? <CategoryIcon /> : <PublicIcon />}
                          label={s.violation_type_id ? "Per Type" : "Global"}
                          size="small"
                          sx={{ fontWeight: 700, backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getTypeLabel(s.violation_type_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                          <Chip label={BILLING_MODE_LABELS[s.billing_mode] || s.billing_mode}
                            size="small" sx={{ fontWeight: 700, backgroundColor: mc.bg, color: mc.color }}
                            icon={s.billing_mode === "escalating" ? <TrendingUpIcon /> : undefined} />
                          {s.billing_code && (
                            <Typography variant="caption" sx={{ color:"#6b778c", fontFamily:"monospace" }}>
                              Code: {s.billing_code}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <Chip icon={<AttachMoneyIcon />}
                            label={`$${Number(s.fine_amount || 0).toFixed(2)}`}
                            size="small"
                            sx={{ fontWeight: 700, backgroundColor: "rgba(34,49,66,0.08)", color: "#223142" }} />
                          {s.billing_mode === "escalating" && s.escalation_amount && (
                            <Typography variant="caption" sx={{ color: "#7c3aed" }}>
                              +${Number(s.escalation_amount).toFixed(2)} / occurrence
                              {s.max_fine ? ` (max $${Number(s.max_fine).toFixed(2)})` : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display:"flex", flexDirection:"column", gap:0.3 }}>
                          <Chip label={TRIGGER_MODE_LABELS[s.trigger_mode] || s.trigger_mode || "Manual"}
                            size="small" variant="outlined"
                            color={s.trigger_mode === "automatic" ? "success" : "default"} />
                          {s.trigger_status_name && (
                            <Typography variant="caption" sx={{ color:"#6b778c" }}>
                              on: {s.trigger_status_name}
                            </Typography>
                          )}
                          {s.recurring_enabled && (
                            <Typography variant="caption" sx={{ color:"#7c3aed", fontWeight:600 }}>
                              Recurring every {s.recurring_interval} {s.recurring_frequency}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#6b778c", fontSize: "0.82rem" }}>
                          {getIntegrationLabel(s.integration_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.is_active ? "Active" : "Inactive"} size="small"
                          color={s.is_active ? "success" : "default"}
                          variant={s.is_active ? "filled" : "outlined"} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(s)}><Edit /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(s.id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
                    No violation billing rules configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.3rem", pt: 3, pb: 1 }}
        >
          {editingId ? "Edit Billing Rule" : "Add Billing Rule"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {/* Scope */}
          <TextField
            select fullWidth label="Rule Scope" name="scope"
            value={form.scope} onChange={handleChange} sx={fieldSx}
          >
            <MenuItem value="global">Global (all violations)</MenuItem>
            <MenuItem value="type">Specific Violation Type</MenuItem>
          </TextField>

          {form.scope === "type" && (
            <TextField
              select fullWidth label="Violation Type" name="violation_type_id"
              value={form.violation_type_id} onChange={handleChange} sx={fieldSx}
            >
              <MenuItem value="">Select Type</MenuItem>
              {violationTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
              ))}
            </TextField>
          )}

          {/* Integration */}
          <TextField
            select fullWidth label="Integration" name="integration_id"
            value={form.integration_id} onChange={handleChange} sx={fieldSx}
            helperText="Optional. link to an accounting integration"
          >
            <MenuItem value="">None</MenuItem>
            {integrations.map((i) => (
              <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>
            ))}
          </TextField>

          {/* Billing mode */}
          <TextField
            select fullWidth label="Billing Mode" name="billing_mode"
            value={form.billing_mode} onChange={handleChange} sx={fieldSx}
          >
            {Object.entries(BILLING_MODE_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </TextField>

          {/* Fine amount */}
          <TextField
            fullWidth
            label={form.billing_mode === "flat" ? "Flat Amount ($)" : "Fine Amount ($)"}
            name="fine_amount" type="number" inputProps={{ min: 0, step: 0.01 }}
            value={form.fine_amount} onChange={handleChange} sx={fieldSx}
          />

          {/* Labor rate */}
          <TextField
            fullWidth label="Additional Labor Rate ($/hr)" name="labor_rate"
            type="number" inputProps={{ min: 0, step: 0.01 }}
            value={form.labor_rate} onChange={handleChange} sx={fieldSx}
            helperText="Optional additional labor cost"
          />

          {/* Escalation fields */}
          {showEscalation && (
            <>
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "12px",
                  backgroundColor: "rgba(124,58,237,0.04)",
                  border: "1px solid rgba(124,58,237,0.15)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.escalation_enabled}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, escalation_enabled: e.target.checked }))
                      }
                    />
                  }
                  label="Enable Escalation"
                />
              </Box>

              {form.escalation_enabled && (
                <>
                  <TextField
                    fullWidth label="Escalation Amount ($ per occurrence)" name="escalation_amount"
                    type="number" inputProps={{ min: 0, step: 0.01 }}
                    value={form.escalation_amount} onChange={handleChange} sx={fieldSx}
                  />
                  <TextField
                    fullWidth label="Maximum Fine Cap ($)" name="max_fine"
                    type="number" inputProps={{ min: 0, step: 0.01 }}
                    value={form.max_fine} onChange={handleChange} sx={fieldSx}
                    helperText="Leave blank for no cap"
                  />
                </>
              )}
            </>
          )}

          {/* Billing code */}
          <TextField
            fullWidth label="Billing Code (Item Group Code)" name="billing_code"
            value={form.billing_code} onChange={handleChange} sx={fieldSx}
            helperText="Jonas or accounting system item group code (e.g. FINE1, VIOL2)"
          />

          {/* Trigger mode */}
          <TextField
            select fullWidth label="Billing Trigger" name="trigger_mode"
            value={form.trigger_mode} onChange={handleChange} sx={fieldSx}
            helperText={
              form.trigger_mode === "automatic"
                ? "Fine and billing record are created automatically on status change"
                : "Staff manually clicks Send to Billing on each fine"
            }
          >
            <MenuItem value="manual">Manual - staff reviews before billing</MenuItem>
            <MenuItem value="automatic">Automatic - triggers on status change</MenuItem>
          </TextField>

          {form.trigger_mode === "automatic" && (
            <TextField
              select fullWidth label="Trigger Status" name="trigger_status_id"
              value={form.trigger_status_id} onChange={handleChange} sx={fieldSx}
              helperText="Which status change fires the automatic fine"
            >
              <MenuItem value="">Any billable status</MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.statusname}</MenuItem>
              ))}
            </TextField>
          )}

          {/* Recurring fine */}
          <Box sx={{ mt:2, px:1.5, py:1.25, borderRadius:"12px", backgroundColor:"rgba(124,58,237,0.04)", border:"1px solid rgba(124,58,237,0.15)" }}>
            <FormControlLabel
              control={
                <Switch checked={form.recurring_enabled}
                  onChange={(e) => setForm((p) => ({ ...p, recurring_enabled: e.target.checked }))} />
              }
              label={<Typography variant="body2" sx={{ fontWeight:600 }}>Recurring Fine</Typography>}
            />
            {form.recurring_enabled && (
              <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:1.5, mt:1 }}>
                <TextField
                  fullWidth label="Interval (number)" name="recurring_interval"
                  type="number" inputProps={{ min:1 }}
                  value={form.recurring_interval} onChange={handleChange}
                  sx={fieldSx} helperText="e.g. 30" />
                <TextField
                  select fullWidth label="Frequency" name="recurring_frequency"
                  value={form.recurring_frequency} onChange={handleChange} sx={fieldSx}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </TextField>
              </Box>
            )}
            {form.recurring_enabled && (
              <Typography variant="caption" sx={{ color:"#7c3aed", mt:0.5, display:"block" }}>
                If the violation is not resolved, a new fine will be created every{" "}
                {form.recurring_interval || "?"} {form.recurring_frequency || "month(s)"}.
              </Typography>
            )}
          </Box>

          {/* Notes */}
          <TextField
            fullWidth label="Notes" name="notes" multiline rows={2}
            value={form.notes} onChange={handleChange} sx={fieldSx}
          />

          {/* Active */}
          <Box sx={{ mt: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={closeDialog} variant="outlined" sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={primaryButtonSx}>
            {editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViolationBillingSetupManager;
